/**
 * DesignTab — Admin-only tab on Job Detail for design jobs.
 *
 * Features:
 * - Design phase list with projected hours, actual hours, clock in/out per phase, complete phase
 * - Manual time entry per phase
 * - Push BOM from parent item (admin, with warning)
 * - Complete Design button (locked until BOM has at least 1 line)
 * - DNV process toggles and phase chain (if parent item is DNV)
 */

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  fetchDesignPhases, createDesignPhase, updateDesignPhase,
  completeDesignPhase, completeDesignJob, pushBomToJob,
  fetchDnvProcesses, createDnvProcess, updateDnvProcess,
  completeDnvProcess, toggleDnvProcess,
  clockIn, clockOut, getActiveClock,
} from '../../../api/jobs';
import { createManualEntry } from '../../../api/timeTracking';
import { Button } from '../../common';

// Full display map: all 6 legacy + current phase types. Retained so
// existing DesignPhase rows carrying a legacy phase_type still render
// with their correct label (line 393 render + line 137 complete-confirm).
// The backend DesignPhase enum is unchanged.
const PHASE_LABELS = {
  skid_drawing: 'Skid Drawing',
  complete_system_drawing: 'Complete System Drawing',
  rd: 'R&D',
  plc_programming: 'PLC Programming',
  software_programming: 'Software Application Programming',
  misc_design: 'Misc Design',
};

// V2-a2 (partial): NEW selections are limited to the 2 current design
// categories. Legacy types remain in PHASE_LABELS above so historical
// rows still render properly.
const SELECTABLE_PHASE_TYPES = ['complete_system_drawing', 'plc_programming'];

const DNV_PROCESS_LABELS = {
  engineering_review: 'Engineering Review & Stamp',
  lrqa_quote: 'LRQA Type Approval Quote',
  lrqa_type_approval: 'LRQA Type Approval',
  fabrication_phase_1: 'Fabrication Phase 1',
  lrqa_production_inspection: 'LRQA Production Inspection',
  drop_test: 'Drop Test',
  fabrication_phase_2: 'Fabrication Phase 2',
  final_lrqa_inspection: 'Final LRQA Inspection',
};

// Historical averages apply to everything except fabrication phases
const DNV_HISTORICAL_STEPS = new Set([
  'engineering_review', 'lrqa_quote', 'lrqa_type_approval',
  'lrqa_production_inspection', 'drop_test', 'final_lrqa_inspection',
]);

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
}

export default function DesignTab({ job, onJobUpdated }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [phases, setPhases] = useState([]);
  const [dnvProcesses, setDnvProcesses] = useState([]);
  const [activeClock, setActiveClock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Add phase modal state
  const [showAddPhase, setShowAddPhase] = useState(false);
  const [newPhaseType, setNewPhaseType] = useState('');
  const [newPhaseHours, setNewPhaseHours] = useState('');

  // Manual time entry state
  const [manualEntryPhaseId, setManualEntryPhaseId] = useState(null);
  const [manualHours, setManualHours] = useState('');
  const [manualDate, setManualDate] = useState('');
  const [manualNotes, setManualNotes] = useState('');

  // Add DNV process modal
  const [showAddDnv, setShowAddDnv] = useState(false);
  const [newDnvType, setNewDnvType] = useState('');
  const [newDnvDays, setNewDnvDays] = useState('');

  // Phase clock tracking
  const [clockingPhaseId, setClockingPhaseId] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [phasesData, clockData] = await Promise.all([
        fetchDesignPhases(job.id),
        getActiveClock(),
      ]);
      setPhases(phasesData);
      setActiveClock(clockData);

      if (job.parent_item_is_dnv) {
        const dnvData = await fetchDnvProcesses(job.id);
        setDnvProcesses(dnvData);
      }
    } catch (e) {
      setError('Failed to load design data.');
    } finally {
      setLoading(false);
    }
  }, [job.id, job.parent_item_is_dnv]);

  useEffect(() => { loadData(); }, [loadData]);

  const existingTypes = new Set(phases.map((p) => p.phase_type));
  // Filter from the pruned selection subset (not all of PHASE_LABELS) so
  // the "Add Phase" dropdown only offers the 2 current categories.
  const availablePhaseTypes = SELECTABLE_PHASE_TYPES
    .filter((k) => !existingTypes.has(k))
    .map((k) => [k, PHASE_LABELS[k]]);
  const existingDnvTypes = new Set(dnvProcesses.map((p) => p.process_type));
  const availableDnvTypes = Object.entries(DNV_PROCESS_LABELS).filter(([k]) => !existingDnvTypes.has(k));

  const bomLineCount = job.bom_summary?.total_lines || 0;

  async function handleClockPhase(phase) {
    if (clockingPhaseId) return;
    setClockingPhaseId(phase.id);
    try {
      if (activeClock?.job === job.id && activeClock?.design_phase === phase.id) {
        await clockOut(job.id);
        setActiveClock(null);
      } else {
        if (activeClock?.job) await clockOut(activeClock.job);
        const result = await clockIn(job.id, phase.id);
        setActiveClock({ job: job.id, design_phase: phase.id, clock_in_at: result?.clock_in_at, ...result });
      }
      await loadData();
    } catch (e) {
      alert(e?.response?.data?.error || 'Clock action failed');
    } finally {
      setClockingPhaseId(null);
    }
  }

  async function handleCompletePhase(phase) {
    if (!window.confirm(`Mark "${PHASE_LABELS[phase.phase_type]}" as complete?`)) return;
    try {
      await completeDesignPhase(phase.id);
      await loadData();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to complete phase');
    }
  }

  async function handleAddPhase() {
    if (!newPhaseType) return;
    setSaving(true);
    try {
      await createDesignPhase({
        job: job.id,
        phase_type: newPhaseType,
        projected_hours: newPhaseHours ? parseFloat(newPhaseHours) : null,
        sort_order: phases.length,
      });
      setShowAddPhase(false);
      setNewPhaseType('');
      setNewPhaseHours('');
      await loadData();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to add phase');
    } finally {
      setSaving(false);
    }
  }

  async function handleManualEntry() {
    if (!manualHours || !manualEntryPhaseId) return;
    setSaving(true);
    try {
      await createManualEntry({
        job: job.id,
        hours: parseFloat(manualHours),
        manual_date: manualDate || undefined,
        notes: manualNotes,
        design_phase_id: manualEntryPhaseId,
      });
      setManualEntryPhaseId(null);
      setManualHours('');
      setManualDate('');
      setManualNotes('');
      await loadData();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to add time entry');
    } finally {
      setSaving(false);
    }
  }

  async function handlePushBom() {
    if (!window.confirm(
      'This will MERGE the parent item\'s default BOM with this job:\n\n' +
      '• Lines with POs, RFQs, or receiving activity will be PRESERVED\n' +
      '• New lines from the parent BOM will be ADDED\n' +
      '• Existing lines without activity may be updated\n\n' +
      'Continue?'
    )) return;
    setSaving(true);
    try {
      const response = await pushBomToJob(job.id);
      // Show summary if available (response is {data: {..., _push_summary: {...}}})
      const summary = response?.data?._push_summary || response?._push_summary;
      if (summary) {
        const msg = [
          summary.lines_added > 0 ? `${summary.lines_added} line(s) added` : null,
          summary.lines_updated > 0 ? `${summary.lines_updated} line(s) updated` : null,
          summary.lines_preserved > 0 ? `${summary.lines_preserved} line(s) preserved (have POs/RFQs)` : null,
        ].filter(Boolean).join(', ');
        if (msg) alert(`✅ BOM merged: ${msg}`);
      }
      onJobUpdated?.();
      await loadData();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to push BOM');
    } finally {
      setSaving(false);
    }
  }

  async function handleCompleteDesign() {
    if (bomLineCount === 0) {
      alert('Cannot complete design: the job has no BOM lines. Import a BOM first.');
      return;
    }
    if (!window.confirm(
      '✅ Complete design and transition this job to the build workflow (Pending BOM)? The design flag will be cleared and the job will continue through the normal build process.'
    )) return;
    setSaving(true);
    try {
      await completeDesignJob(job.id);
      onJobUpdated?.();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to complete design');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleDnv(proc) {
    try {
      await toggleDnvProcess(proc.id, !proc.is_required);
      await loadData();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to toggle DNV process');
    }
  }

  async function handleCompleteDnv(proc) {
    if (!window.confirm(`Mark "${DNV_PROCESS_LABELS[proc.process_type]}" as complete?`)) return;
    try {
      await completeDnvProcess(proc.id);
      await loadData();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to complete DNV step');
    }
  }

  async function handleAddDnv() {
    if (!newDnvType) return;
    setSaving(true);
    try {
      await createDnvProcess({
        job: job.id,
        process_type: newDnvType,
        is_required: true,
        projected_days: newDnvDays ? parseFloat(newDnvDays) : null,
      });
      setShowAddDnv(false);
      setNewDnvType('');
      setNewDnvDays('');
      await loadData();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to add DNV process');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateHours(phase, value) {
    try {
      await updateDesignPhase(phase.id, { projected_hours: value ? parseFloat(value) : null });
      await loadData();
    } catch { /* ignore inline update errors */ }
  }

  async function handleUpdateDnvDays(proc, value) {
    try {
      await updateDnvProcess(proc.id, { projected_days: value ? parseFloat(value) : null });
      await loadData();
    } catch { /* ignore */ }
  }

  if (!isAdmin) {
    return (
      <div className="py-12 text-center text-gray-400 text-sm">
        Design management is restricted to administrators.
      </div>
    );
  }

  if (loading) {
    return <div className="py-8 text-center text-gray-400 text-sm">Loading design data…</div>;
  }

  const currentClockPhaseId = activeClock?.design_phase;
  const isClockedIntoThisJob = activeClock?.job === job.id;

  return (
    <div className="space-y-8">

      {/* ── Header actions ── */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-gray-800">Design Management</h3>
          <p className="text-xs text-gray-500 mt-0.5">Track design phases, log time, and transition to build when ready.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePushBom}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-50"
          >
            📥 Push BOM from Parent Item
          </button>
          <button
            onClick={handleCompleteDesign}
            disabled={saving || bomLineCount === 0}
            title={bomLineCount === 0 ? 'Import a BOM before completing design' : 'Transition this job to the build workflow'}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md border transition-colors disabled:opacity-50 ${
              bomLineCount > 0
                ? 'border-emerald-400 text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                : 'border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed'
            }`}
          >
            ✅ Complete Design
            {bomLineCount === 0 && <span className="ml-1 text-[10px] font-normal">(No BOM)</span>}
          </button>
        </div>
      </div>

      {/* BOM status banner */}
      <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
        bomLineCount > 0
          ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
          : 'bg-amber-50 border border-amber-200 text-amber-700'
      }`}>
        {bomLineCount > 0
          ? `✅ BOM has ${bomLineCount} line${bomLineCount !== 1 ? 's' : ''} — ready to complete design`
          : '⚠️ No BOM lines yet. Use "Push BOM from Parent Item" after importing the BOM.'}
      </div>

      {/* ══════ DESIGN PHASES ══════ */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Design Phases</h4>
          {availablePhaseTypes.length > 0 && (
            <button
              onClick={() => setShowAddPhase(true)}
              className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              + Add Phase
            </button>
          )}
        </div>

        {phases.length === 0 ? (
          <div className="text-sm text-gray-400 italic py-4 text-center border border-dashed border-gray-200 rounded-lg">
            No design phases added yet.
          </div>
        ) : (
          <div className="space-y-3">
            {phases.map((phase) => {
              const isActivePhase = isClockedIntoThisJob && currentClockPhaseId === phase.id;
              const actualHours = parseFloat(phase.actual_hours || 0);
              const projectedHours = parseFloat(phase.projected_hours || 0);
              const pct = projectedHours > 0 ? Math.min((actualHours / projectedHours) * 100, 100) : null;

              return (
                <div
                  key={phase.id}
                  className={`border rounded-lg p-4 ${
                    phase.is_complete
                      ? 'border-emerald-200 bg-emerald-50'
                      : isActivePhase
                      ? 'border-green-300 bg-green-50 ring-1 ring-green-300'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-semibold text-sm ${phase.is_complete ? 'text-emerald-700 line-through' : 'text-gray-800'}`}>
                          {PHASE_LABELS[phase.phase_type] || phase.phase_type}
                        </span>
                        {phase.is_complete && (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                            ✓ Complete {phase.completed_at ? `(${fmtDate(phase.completed_at)})` : ''}
                          </span>
                        )}
                        {isActivePhase && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium animate-pulse">
                            ● Clocked In
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                        <span>
                          <span className="font-medium text-gray-700">{actualHours.toFixed(1)}h</span> actual
                        </span>
                        <span className="flex items-center gap-1">
                          Projected:
                          <input
                            type="number"
                            step="0.25"
                            min="0"
                            defaultValue={phase.projected_hours || ''}
                            onBlur={(e) => handleUpdateHours(phase, e.target.value)}
                            disabled={phase.is_complete}
                            placeholder="—"
                            className="w-16 text-xs border border-gray-200 rounded px-1.5 py-0.5 text-gray-700 disabled:bg-gray-50 disabled:text-gray-400"
                          />
                          <span>h</span>
                        </span>
                        {pct !== null && (
                          <div className="flex items-center gap-1.5 flex-1 max-w-[140px]">
                            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full ${pct >= 100 ? 'bg-red-500' : 'bg-purple-500'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className={`text-[11px] font-bold ${pct >= 100 ? 'text-red-600' : 'text-purple-700'}`}>
                              {pct.toFixed(0)}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {!phase.is_complete && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => setManualEntryPhaseId(manualEntryPhaseId === phase.id ? null : phase.id)}
                          className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded border border-gray-200 hover:bg-gray-50"
                        >
                          + Log Hours
                        </button>
                        <button
                          onClick={() => handleClockPhase(phase)}
                          disabled={!!clockingPhaseId}
                          className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                            isActivePhase
                              ? 'bg-red-500 text-white hover:bg-red-600'
                              : 'bg-purple-500 text-white hover:bg-purple-600'
                          } disabled:opacity-50`}
                        >
                          {isActivePhase ? 'Clock Out' : 'Clock In'}
                        </button>
                        <button
                          onClick={() => handleCompletePhase(phase)}
                          className="text-xs font-medium px-2 py-1.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        >
                          ✓ Done
                        </button>
                      </div>
                    )}
                  </div>

                  {manualEntryPhaseId === phase.id && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-end gap-2 flex-wrap">
                        <div>
                          <label className="block text-[10px] text-gray-400 mb-0.5">Hours *</label>
                          <input
                            type="number" step="0.25" min="0.25"
                            value={manualHours}
                            onChange={(e) => setManualHours(e.target.value)}
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                            placeholder="0.5"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-400 mb-0.5">Date</label>
                          <input
                            type="date" value={manualDate}
                            onChange={(e) => setManualDate(e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                        </div>
                        <div className="flex-1 min-w-[120px]">
                          <label className="block text-[10px] text-gray-400 mb-0.5">Notes</label>
                          <input
                            type="text" value={manualNotes}
                            onChange={(e) => setManualNotes(e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                            placeholder="Optional"
                          />
                        </div>
                        <button
                          onClick={handleManualEntry}
                          disabled={!manualHours || saving}
                          className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setManualEntryPhaseId(null)}
                          className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {showAddPhase && availablePhaseTypes.length > 0 && (
          <div className="mt-3 p-4 border border-blue-200 rounded-lg bg-blue-50">
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Phase Type *</label>
                <select
                  value={newPhaseType}
                  onChange={(e) => setNewPhaseType(e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
                >
                  <option value="">Select phase...</option>
                  {availablePhaseTypes.map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Projected Hours</label>
                <input
                  type="number" step="0.25" min="0"
                  value={newPhaseHours}
                  onChange={(e) => setNewPhaseHours(e.target.value)}
                  className="w-24 border border-gray-300 rounded px-2 py-1.5 text-sm"
                  placeholder="e.g. 4"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAddPhase}
                  disabled={!newPhaseType || saving}
                  className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Add Phase
                </button>
                <button
                  onClick={() => { setShowAddPhase(false); setNewPhaseType(''); setNewPhaseHours(''); }}
                  className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ══════ DNV PROCESSES ══════ */}
      {job.parent_item_is_dnv && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide flex items-center gap-2">
              🏷 DNV 2.71 Certification Process
              <span className="text-xs font-normal text-gray-400 normal-case tracking-normal">— Toggle steps on/off as needed</span>
            </h4>
            {availableDnvTypes.length > 0 && (
              <button
                onClick={() => setShowAddDnv(true)}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                + Add Step
              </button>
            )}
          </div>

          {dnvProcesses.length === 0 ? (
            <div className="text-sm text-gray-400 italic py-4 text-center border border-dashed border-gray-200 rounded-lg">
              No DNV process steps added yet.
            </div>
          ) : (
            <div className="space-y-2">
              {dnvProcesses.map((proc, idx) => (
                <div
                  key={proc.id}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-sm ${
                    !proc.is_required
                      ? 'border-gray-100 bg-gray-50 opacity-60'
                      : proc.is_complete
                      ? 'border-emerald-200 bg-emerald-50'
                      : proc.started_at
                      ? 'border-blue-200 bg-blue-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <span className="text-xs font-bold text-gray-400 w-5 text-center flex-shrink-0">{idx + 1}</span>
                  <button
                    onClick={() => handleToggleDnv(proc)}
                    className={`flex-shrink-0 w-8 h-4 rounded-full transition-colors relative ${proc.is_required ? 'bg-blue-500' : 'bg-gray-300'}`}
                    title={proc.is_required ? 'Required — click to skip' : 'Skipped — click to require'}
                  >
                    <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-all ${proc.is_required ? 'left-4' : 'left-0.5'}`} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-medium ${!proc.is_required ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                        {DNV_PROCESS_LABELS[proc.process_type] || proc.process_type}
                      </span>
                      {proc.is_complete && (
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                          ✓ Complete {proc.completed_at ? `(${fmtDate(proc.completed_at)})` : ''}
                        </span>
                      )}
                      {!proc.is_complete && proc.started_at && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                          ● In Progress (started {fmtDate(proc.started_at)})
                        </span>
                      )}
                      {DNV_HISTORICAL_STEPS.has(proc.process_type) && proc.historical_avg_days && (
                        <span className="text-[10px] text-gray-400">Avg: {proc.historical_avg_days}d</span>
                      )}
                    </div>
                    {proc.projected_end_date && !proc.is_complete && (
                      <div className="text-[11px] text-gray-400 mt-0.5">Projected: {fmtDate(proc.projected_end_date)}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <input
                      type="number" step="0.5" min="0"
                      defaultValue={proc.projected_days || ''}
                      onBlur={(e) => handleUpdateDnvDays(proc, e.target.value)}
                      disabled={proc.is_complete}
                      placeholder="Days"
                      className="w-16 text-xs border border-gray-200 rounded px-1.5 py-0.5 text-gray-700 disabled:bg-gray-50 disabled:text-gray-400"
                    />
                    <span className="text-xs text-gray-400">d</span>
                  </div>
                  {proc.is_required && !proc.is_complete && (
                    <button
                      onClick={() => handleCompleteDnv(proc)}
                      className="flex-shrink-0 px-2 py-1 text-xs font-medium border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50"
                    >
                      ✓ Done
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {showAddDnv && availableDnvTypes.length > 0 && (
            <div className="mt-3 p-4 border border-blue-200 rounded-lg bg-blue-50">
              <div className="flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Process Step *</label>
                  <select
                    value={newDnvType}
                    onChange={(e) => setNewDnvType(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
                  >
                    <option value="">Select step...</option>
                    {availableDnvTypes.map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Projected Days</label>
                  <input
                    type="number" step="0.5" min="0"
                    value={newDnvDays}
                    onChange={(e) => setNewDnvDays(e.target.value)}
                    className="w-24 border border-gray-300 rounded px-2 py-1.5 text-sm"
                    placeholder="e.g. 7"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleAddDnv}
                    disabled={!newDnvType || saving}
                    className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    Add Step
                  </button>
                  <button
                    onClick={() => { setShowAddDnv(false); setNewDnvType(''); setNewDnvDays(''); }}
                    className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
